import { IconProp } from "@fortawesome/fontawesome-svg-core"
import { faGlobe, faLayerGroup, faUserLock } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Button, Container, Grid, IconButton, Tooltip, Typography } from "@mui/material"
import { Row } from "@tanstack/react-table"
import { hasRole } from "auth/hasRole"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { TableLoadingStateEnum } from "components/ClpTable/tableProps.interface"
import NoResultFound from "components/Dialogs/NoResultFound"
import PaperBox from "components/PaperBox"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import { useContext, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Moment from "react-moment"
import { useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import LoadingPage from "../../components/Loading/LoadingPage"
import FolderForm from "./components/Form/FolderForm"
import { IDocumentFolder, IDocumentFormValues } from "./interface/folder.interface"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import ContinueDialog from "components/Dialogs/ContinueDialog"

const Documents = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const [foldersResult, listFoldersApi] = useApi() as [IApiResult, IApiFunc]
  const [showContinueModal, setShowContinueModal] = useState(false)
  const [addFolderResult, addFolder] = useApi() as [IApiResult, IApiFunc]
  const [deleteResult, deleteFolder] = useApi() as [IApiResult, IApiFunc]
  const [selectedFolder, setSelectedFolder] = useState({
    id: 0,
    name: ""
  })

  const handleSubmit = (values: IDocumentFormValues) => {
    addFolder.post(apiEndpoints.documents.adddocument, values)
  }

  useEffect(() => {
    if (addFolderResult.status === 2) {
      listFoldersApi.get(
        apiEndpoints.documents.listfolders.replace("{companyId}", appContext.currentCompanyId)
      )
      appContext.toggleSideBar()
    }
  }, [addFolderResult])

  useEffect(() => {
    listFoldersApi.get(apiEndpoints.documents.listfolders.replace("{companyId}", appContext.currentCompanyId))
  }, [listFoldersApi, appContext.currentCompanyId])

  const removeFile = (id: any) => {
    deleteFolder.del(apiEndpoints.documents.deletedocument.replace("{id}", id))
  }

  const handleShowContinueModal = (id: number, name: string) => {
    setSelectedFolder({ id, name })
    setShowContinueModal(true)
  }

  useEffect(() => {
    if (deleteResult.status === 2) {
      setShowContinueModal(false)
      listFoldersApi.get(
        apiEndpoints.documents.listfolders.replace("{companyId}", appContext.currentCompanyId)
      )
    }
  }, [deleteResult])

  const columns = useMemo(() => {
    const columns = [
      {
        accessorKey: "permissions",
        header: "",
        accessorFn: (row: any) => {
          return row.permissions?.length > 0
            ? row.permissions.map((x: string) => x).join(", ")
            : t("documents.shared", "Shared")
        },
        cell: ({ row }: any) => {
          return row.original?.permissions?.length > 0 ? (
            <Tooltip title={row.original?.permissions.map((x: string) => x).join(", ")}>
              <IconButton color="primary" size="small">
                <FontAwesomeIcon color={(theme.palette as any).primary[700]} icon={faUserLock as IconProp} />
              </IconButton>
            </Tooltip>
          ) : row.original?.isAvailableForAll ? (
            <Tooltip title={t("documents.shared_with_all", "Shared with all")}>
              <IconButton color="primary" size="small">
                <FontAwesomeIcon
                  color={(theme.palette as any).primary[700]}
                  icon={faGlobe as IconProp}
                />{" "}
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t("documents.document_inactive", "Document inactive")}>
              <IconButton color="info" size="small">
                <FontAwesomeIcon color={(theme.palette as any).grey[400]} icon={faGlobe as IconProp} />
              </IconButton>
            </Tooltip>
          )
        }
      },
      {
        accessorKey: "name",
        header: t("global.folder", "Folder")
      },
      {
        accessorKey: "totalAttachments",
        header: t("global.file.files", "Files"),
        cell: ({ row }: any) => {
          return (
            <>
              {row.original.totalAttachments} {t("global.object", "Object")}
            </>
          )
        }
      },
      {
        accessorKey: "createdByUser.name",
        header: t("global.created_by", "Created by")
      },
      {
        accessorKey: "last_updated",
        header: t("documents.last_updated", "Last updated"),
        cell: ({ row }: any) => {
          return (
            row.original.updatedDate && (
              <Moment utc local format="YYYY-MM-DD">
                {row.original.updatedDate}
              </Moment>
            )
          )
        }
      }
    ]

    const hasDocumentAdmin = hasRole(appContext.appUser, allRoles.DocumentsAdmin)
    if (hasDocumentAdmin) {
      columns.push({
        accessorKey: "id",
        header: "",
        cell: ({ row }: any) => {
          return (
            <TableButton
              type={TableButtonTypeEnum.Delete}
              handleOnClick={() => {
                handleShowContinueModal(row.original.id, row.original.name)
              }}
            />
          )
        }
      })
    }
    return columns
  }, [appContext.appUser])

  const initialValues: IDocumentFolder = {
    id: 0,
    name: "",
    description: "",
    body: "",
    isVisibleStartPage: false,
    companyIds: [],
    departmentIds: [],
    customRoleIds: [],
    attachments: [],
    totalAttachments: 0,
    folderId: null,
    folder: null,
    cretedDate: new Date()
  }
  return foldersResult.status === 1 ? (
    <Container>
      <LoadingPage />
    </Container>
  ) : (
    <div>
      <Container>
        {showContinueModal && (
          <ContinueDialog
            result={deleteResult}
            setShowContinueModal={setShowContinueModal}
            onAddSubmit={() => removeFile(selectedFolder.id)}
            message={
              <>
                {t("documents.delete_folder", "Are you sure you want to delete the folder?")}{" "}
                <strong>{selectedFolder && selectedFolder.name}</strong>{" "}
                {t("documents.uploaded_files", "and all uploaded files, your choice cannot be undone?")}
              </>
            }
            continueBtnText={t("documents.yes_delete", "Yes delete")}
            cancelBtnText={t("global.buttons.cancel", "Cancel")}
          />
        )}
        {hasRole(appContext.appUser, allRoles.DocumentsAdmin) && (
          <FolderForm formValues={initialValues} handleSubmit={handleSubmit} results={addFolderResult} />
        )}

        <Box>
          <Box display="flex" flexDirection="row" alignItems="center">
            <Box order={2} flexGrow={2}>
              <Grid item>
                <Typography variant="h1">{t(`documents.title`, "Documents")}</Typography>
                <Typography variant="paragraphMedium" color="textSecondary">
                  {
                    appContext.appUser.customer.companies.find(
                      (x: any) => x.id === appContext.currentCompanyId
                    )?.name
                  }
                </Typography>
              </Grid>
            </Box>
            <Box order={3}>
              {hasRole(appContext.appUser, allRoles.DocumentsAdmin) && (
                <Button onClick={() => appContext.toggleSideBar(true)} variant="contained" color="primary">
                  {t(`documents.add_button`, "Add folder")}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        {foldersResult.status === 2 && foldersResult.data.length === 0 && (
          <PaperBox>
            <NoResultFound
              icon={faLayerGroup as IconProp}
              message={
                hasRole(appContext.appUser, allRoles.DocumentsAdmin)
                  ? t(
                      `documents.no_folder_created_admin`,
                      "No folders have been created, click add folder to create a new one"
                    )
                  : t(`documents.no_folder_created`, "No folders have been created")
              }
              backgroundColor={(theme.palette as any).primary[300]}
              color={(theme.palette as any).primary[800]}
              size="lg"
            />
          </PaperBox>
        )}
      </Container>
      <StyledTableContainer>
        <ClpTable
          onRowClick={(row: Row<any>) => history.push(allRoutes.DocumentsDetails(row.original.id))}
          rows={foldersResult.status === 2 && foldersResult.data}
          loadingState={foldersResult.status as TableLoadingStateEnum}
          columns={columns}
          tableId={ClpTableEnum.Documents}
        />
      </StyledTableContainer>
    </div>
  )
}
export default Documents
